import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Dropdown, Form } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { RiDeleteBinLine } from "react-icons/ri";
import {
    CloseSettingsDocument, GetSettingsDocument, GetSeverityLabelsDocument, SettingsSlaTierInput,
    SettingsSlaTierMetricValue,
    SettingsSlaTierMetricValueInput,
    SettingsSlaTierValue, SlaMetric, TagsSettingsDocument, useSaveSettingsMutation,
} from "../../../../__generated__/api";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import EditableControl from "../../../../components/editable-text/EditableText";
import Popover from "../../../../components/popover/Popover";
import Select from "../../../../components/select/Select";
import TagList from "../../../../components/tag-list/TagList";
import useArticleContext from "../../../../contexts/HelpContext";
import { timeToHours } from "../../../../date";
import { SettingsProps } from "../base";
import Styles from "./SLA.module.scss";

const SECOND_TO_MS = 1000 * 60;
const MINUTE_TO_MS = SECOND_TO_MS * 60;

const POLICIES = [
    {
        metric: SlaMetric.TimeToFirstResponse,
        title: "Time to first response",
        description: (
            <div>
                This is the amount of time it takes for a
                support agent to respond to a customer&apos;s initial inquiry.
                Foqal Agent&apos;s time to
                first response policy helps you ensure that a support agent responds to the
                customer&apos;s request within a specific time frame.
            </div>
        ),
    },
    {
        metric: SlaMetric.TimeToUpdate,
        title: "Time to update",
        description: (
            <div>
                This policy ensures that an agent updates the customer periodically,
                usually within a specific time frame. Time to
                update policy helps to keep customers informed
                and up-to-date on the status of their request. For example, in the case
                of a major emergency, a customer might ask to be updated every 4 hours
                until the issue is resolved. This policy helps you keep track of how much
                time elapsed since the last customer update.
            </div>
        ),
    },
    {
        metric: SlaMetric.RequesterWaitTime,
        title: "Requester Wait Time",
        description: (
            <div>
                This policy measures the amount of time it takes to update the requester
                after the requester adds a subsequent message after the request.
                Foqal Agent&apos;s requester wait time policy helps ensure that the requester
                receives a response within a specific time frame, after they add
                additional context or an additional question.
            </div>
        ),
    },
];

function timeToMilliseconds(value: string): number {
    return timeToHours(value) * SECOND_TO_MS;
}

function SLAHourMinute({ policy, onChange }: {
    policy: SettingsSlaTierMetricValue;
    onChange: (value: SettingsSlaTierMetricValueInput) => void
}): ReactElement {
    const [value, setValue] = useState(policy.value ?? 0);
    const [shouldAccountHours, setShouldAccountHours] = useState(policy.shouldAccountHoursOfOperation ?? false);

    useEffect(() => {
        setValue(policy.value ?? 0);
        setShouldAccountHours(policy.shouldAccountHoursOfOperation ?? false);
    }, [policy]);

    useEffect(() => {
        if (policy.value !== value || policy.shouldAccountHoursOfOperation !== shouldAccountHours) {
            onChange({
                ...policy,
                value,
                metric: policy.metric,
                shouldAccountHoursOfOperation: shouldAccountHours,
            });
        }
    }, [value, shouldAccountHours]);

    const currentHour = Math.floor((value ?? 0) / MINUTE_TO_MS);
    const currentMin = Math.floor((value ?? 0) / SECOND_TO_MS) % 60;

    return (
        <div>
            <div className="d-flex justify-content-center align-items-center border border-light rounded w-auto mx-4">
                <input
                    type="number"
                    placeholder="hrs"
                    required
                    className={`border-0 p-1 rounded text-end ${Styles["time-input"]} me-1`}
                    onChange={current => setValue(current.target.value?.length > 0
                        ? ((parseInt(current.target.value, 10) ?? 0) * MINUTE_TO_MS + ((value ?? 0) % MINUTE_TO_MS))
                        : 0)}
                    value={value != null ? currentHour.toString().padStart(2, "0") : ""}
                    onClick={el => (el.target as HTMLInputElement).select()}
                />
                <span>{" : "}</span>
                <input
                    type="number"
                    placeholder="min"
                    max={60}
                    required
                    className={`border-0 p-1 rounded text-start ${Styles["time-input"]} ms-1`}
                    onChange={current => setValue(current.target.value?.length > 0
                        ? (
                            (currentHour * MINUTE_TO_MS)
                        + (Math.min(parseInt(current.target.value, 10) ?? 0, 59) * SECOND_TO_MS)
                        )
                        : 0)}
                    value={value != null ? currentMin.toString().padStart(2, "0") : ""}
                    onClick={el => (el.target as HTMLInputElement).select()}
                />

            </div>
            <small className="mx-4 d-block">
                <Form.Check
                    type="switch"
                    label="Hours of Operation"
                    className="mx-1"
                    checked={shouldAccountHours}
                    onChange={event => setShouldAccountHours(event.target.checked)}
                />
            </small>
        </div>
    );
}

interface SLATierRowProps {
    label: string;
    value: SettingsSlaTierValue;
    onChange: (value: SettingsSlaTierValue) => void
}

function SLATierValueRow(props: SLATierRowProps): ReactElement {
    const policyMap = props.value?.policies?.toIdMap("metric") ?? {};
    const policies = POLICIES.map(metric => policyMap[metric.metric] ?? { metric: metric.metric } as SettingsSlaTierMetricValue);
    return (
        <div className="d-flex mb-2">
            <strong className="col-1 text-end pe-3">{props.label}</strong>
            {policies.map(policy => (
                <div className="col-2">
                    <SLAHourMinute
                        policy={policy}
                        onChange={value => {
                            const copy = [...props.value?.policies ?? []];
                            const current = copy.findIndex(row => row.metric === value.metric);
                            if (current < 0) {
                                copy.push({
                                    ...value,
                                    __typename: undefined,
                                });
                            } else {
                                copy[current] = {
                                    ...value,
                                    __typename: undefined,
                                };
                            }

                            props.onChange({
                                policies: copy,
                                __typename: undefined,
                            });
                        }}
                    />
                </div>
            ))}

        </div>
    );
}

interface SLATierProps {
    name: string;
    description: string | undefined;
    severityLabels: string[];
    severityValues: SettingsSlaTierValue[];
    onChange: (value: ({name?: string; description?: string; values?: SettingsSlaTierValue[]})) => void
    onDelete: () => void
}

function SLATierRow(props: SLATierProps): ReactElement {
    const { severityValues } = props;
    return (
        <div className="mb-5 pb-5 list-group-item">
            <div className="d-flex justify-content-between">

                <h4>
                    <EditableControl
                        isFormField
                        placeholder="SLA Policy name"
                        value={props.name}
                        isEditing={props.name === ""}
                        onChange={value => props.onChange({
                            name: value as string,
                            description: props.description,
                            values: severityValues,
                        })}
                    />
                </h4>
                <button type="button" className="btn btn-none" onClick={() => props.onDelete()}>
                    <RiDeleteBinLine className="text-gray-600" />
                </button>
            </div>
            <div className="d-flex mb-3 justify-content-between text-light">
                <EditableControl
                    isFormField
                    placeholder="SLA Policy description"
                    shouldFocus={false}
                    value={props.description}
                    isEditing={props.description === ""}
                    onChange={value => props.onChange({
                        description: value as string,
                        name: props.name,
                        values: severityValues,
                    })}
                />

            </div>

            <div className="d-flex mb-3">
                <div className="col-1" />
                {POLICIES.map(policy => (
                    <strong key={policy.metric} className="col-2 text-center text-primary font-weight-bold">
                        {policy.title}
                        <Popover title={policy.title} size="sm" buttonClassName="text-light ms-2">
                            {policy.description}
                        </Popover>
                    </strong>
                ))}
            </div>

            {props.severityLabels.map((label, index) => (
                <SLATierValueRow
                    key={label}
                    label={label}
                    value={severityValues[index]}
                    onChange={value => {
                        severityValues[index] = value;
                        props.onChange({
                            name: props.name,
                            description: props.description,
                            values: Array.from(props.severityValues),
                        });
                    }}
                />
            ))}
        </div>
    );
}
export default function SLASettings(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:c13ef689cc9f415eba9c98c04d5f3312");

    const [severityLabels, setSeverityLabels] = useState(props.settings?.sla?.severityLabels ?? []);
    const [defaultSeverityIndex, setDefaultSeverityIndex] = useState(props.settings?.sla?.defaultSeverityIndex ?? 0);
    const [defaultSLAPolicyIndex, setDefaultSLAPolicyIndex] = useState(0);

    const [slaValues, setSlaValues] = useState<SettingsSlaTierInput[]>([]);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "SLA settings saved.",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: CloseSettingsDocument },
                        { query: TagsSettingsDocument },
                        { query: GetSettingsDocument },
                        { query: GetSeverityLabelsDocument },
                    ],
                    variables: {
                        sla: {
                            severityLabels,
                            defaultSeverityIndex,
                            defaultSLAPolicyIndex,
                            tiers: slaValues,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchDeepCompareProperty(severityLabels ?? [], props.settings?.sla?.severityLabels ?? []);
    dirtyTracker.useWatchProperty(defaultSeverityIndex ?? 0, props.settings?.sla?.defaultSeverityIndex ?? 0);
    dirtyTracker.useWatchProperty(
        defaultSLAPolicyIndex ?? 0,
        props.settings?.sla?.tiers?.findIndex(tier => tier.id === props.settings?.sla?.defaultSLAPolicyId) ?? 0,
    );
    dirtyTracker.useWatchDeepCompareProperty(slaValues, props.settings?.sla?.tiers);

    dirtyTracker.useLoad(() => {
        setSeverityLabels(props.settings?.sla?.severityLabels ?? []);
        setDefaultSeverityIndex(props.settings?.sla?.defaultSeverityIndex ?? 0);
        setDefaultSLAPolicyIndex(
            props.settings?.sla?.tiers?.findIndex(tier => tier.id === props.settings?.sla?.defaultSLAPolicyId) ?? 0,
        );

        setSlaValues(props.settings?.sla?.tiers?.map(tier => ({
            id: tier.id,
            name: tier.name,
            description: tier.description,
            values: tier.values?.map(value => ({
                policies: value?.policies?.map(policy => ({
                    ...policy,
                    __typename: undefined,
                })) ?? [],
            })) ?? [],
        } as SettingsSlaTierInput)) ?? []);
    }, [props?.settings]);

    const createNewTier = () => setSlaValues(current => ([...current, {
        name: "",
        description: "",
        values: [],
    }]));

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">

                <h5 className="mb-3 text-light">
                    Severity
                </h5>

                <Dropdown className="mb-3">
                    <Dropdown.Toggle variant="" className="p-0 my-2">
                        Presets
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setSeverityLabels(["P0", "P1", "P2", "P3"])}>
                            P0 &rarr; P4
                        </Dropdown.Item>

                        <Dropdown.Item onClick={() => setSeverityLabels(["Blocker", "Critical", "Major", "Low"])}>
                            Blocker &rarr; Low
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Severities</Form.Label>
                    <TagList
                        value={severityLabels as string[] ?? []}
                        onChange={value => setSeverityLabels(value)}
                        canNotHaveDuplicates
                        placeholder="Add a severity"
                    />
                    <Form.Text>
                        What are the severity levels?
                    </Form.Text>
                </Form.Group>
                {severityLabels.isNotEmpty && (
                    <Form.Group>
                        <Form.Label className="fw-bolder">Default Severity</Form.Label>
                        <Select
                            options={severityLabels.map((label, index) => ({
                                label,
                                value: index.toString(),
                            }))}
                            value={defaultSeverityIndex >= 0 ? ({
                                label: severityLabels[defaultSeverityIndex],
                                value: defaultSeverityIndex.toString(),
                            }) : undefined}
                            onChange={current => setDefaultSeverityIndex(parseInt(current.value, 10))}
                            placeholder="What is your default severity?"
                        />
                        <Form.Text>
                            What should be the default severity of a ticket if a severity is not yet specified?
                        </Form.Text>
                    </Form.Group>
                )}

            </div>
            {severityLabels.isNotEmpty && (
                <div className="border p-3 rounded mb-5 bg-white">

                    {slaValues.isEmpty ? (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                            <h5 className="text-light">
                                SLA Policies
                            </h5>
                            <div className="d-flex my-3">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={createNewTier}
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Add SLA Policy
                                </button>
                                <Dropdown>
                                    <Dropdown.Toggle variant="light" className="btn-sm ms-4 text-white">
                                        Examples
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => {
                                            setSlaValues([
                                                {
                                                    name: "Enterprise",
                                                    description: "Top tier for the best customers",
                                                    values: severityLabels.map((__, severityIndex) => ({
                                                        policies: [
                                                            {
                                                                metric: SlaMetric.TimeToFirstResponse,
                                                                shouldAccountHoursOfOperation: false,
                                                                value: severityIndex === 0
                                                                    ? 30 * SECOND_TO_MS
                                                                    : timeToMilliseconds(`${2 ** (severityIndex - 1)}:00`),
                                                            },
                                                            {
                                                                metric: SlaMetric.RequesterWaitTime,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: timeToMilliseconds(`04:00`),
                                                            },
                                                        ],
                                                    })),
                                                },
                                                {
                                                    name: "Premium",
                                                    description: "Second highest for the 2nd best customers",
                                                    values: severityLabels.map((__, severityIndex) => ({
                                                        policies: [
                                                            {
                                                                metric: SlaMetric.TimeToFirstResponse,
                                                                shouldAccountHoursOfOperation: false,
                                                                value: timeToMilliseconds(`${2 ** (severityIndex + 0)}:00`),
                                                            },
                                                            {
                                                                metric: SlaMetric.RequesterWaitTime,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: timeToMilliseconds(`08:00`),
                                                            },
                                                        ],

                                                    })),
                                                },
                                                {
                                                    name: "Free",
                                                    description: "Free tier for free customers. ",
                                                    values: severityLabels.map(() => ({
                                                        policies: [
                                                            {
                                                                metric: SlaMetric.TimeToFirstResponse,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: timeToMilliseconds("00:00"),
                                                            },
                                                            {
                                                                metric: SlaMetric.RequesterWaitTime,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: timeToMilliseconds("00:00"),
                                                            },
                                                        ],
                                                    })),
                                                },
                                            ]);
                                            setDefaultSLAPolicyIndex(0);
                                        }}
                                        >
                                            3 Tiers External
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => {
                                            setSlaValues([
                                                {
                                                    name: "Internal",
                                                    description: "SLA policy for internal support",
                                                    values: severityLabels.map((__, severityIndex) => ({
                                                        policies: [
                                                            {
                                                                metric: SlaMetric.TimeToFirstResponse,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: severityIndex === 0
                                                                    ? 30
                                                                    : timeToMilliseconds(`${2 ** (severityIndex - 1)}:00`),
                                                            },
                                                            {
                                                                metric: SlaMetric.TimeToUpdate,
                                                                shouldAccountHoursOfOperation: true,
                                                                value: timeToMilliseconds(`04:00`),
                                                            },
                                                        ],

                                                    })),
                                                },
                                            ]);
                                            setDefaultSLAPolicyIndex(0);
                                        }}
                                        >
                                            Internal Team
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>

                            </div>
                            <small>
                                Defining SLA Policies helps your team prioritize the most important requests
                                for the most important customers. Create a policy, define the time alloted for
                                first response or update, and get notified when SLA policies are breached.
                            </small>
                        </div>
                    ) : (
                        <>
                            <h5 className="mb-3 text-light">
                                SLA Policies
                            </h5>
                            <small className="text-light">
                                Customer support Service Level Agreement (SLA) Policies are agreements
                                between a company and its customers that define the expected level of
                                service quality and support provided by the company to its customers.
                                These policies aim to establish clear expectations for customer support,
                                including the level of response time, problem resolution, and communication.
                            </small>
                            <small className="d-block mt-2 text-light">
                                *  Hours of operations - when calculating the SLA due date, should it include Hours
                                of operation? For example, if the SLA is 16 hours but your Hours of operation are
                                9-5, the 16 hour SLA will be scheduled for 2 days from now instead of 1.
                            </small>

                            {slaValues?.length > 1 && (
                                <Form.Group>
                                    <Form.Label className="fw-bolder">Default Policy</Form.Label>
                                    <Select
                                        options={slaValues.map((tier, index) => ({
                                            label: tier.name ?? "",
                                            value: index.toString(),
                                        }))}
                                        value={defaultSLAPolicyIndex >= 0 ? ({
                                            label: slaValues[defaultSLAPolicyIndex]?.name ?? "Untitled",
                                            value: defaultSLAPolicyIndex.toString(),
                                        }) : undefined}
                                        onChange={current => setDefaultSLAPolicyIndex(parseInt(current.value, 10))}
                                        placeholder="What is your default policy?"
                                    />
                                    <Form.Text>
                                        What should be the default policy when a policy is not explicitly specified for a channel?
                                    </Form.Text>
                                </Form.Group>
                            )}

                            <div className="list-group list-group-flush mt-4">

                                {slaValues.map((value, index) => (
                                    <SLATierRow
                                        key={value.id}
                                        name={value.name ?? ""}
                                        description={value.description ?? ""}
                                        severityLabels={severityLabels}
                                        severityValues={value.values ?? []}
                                        onChange={tierValue => setSlaValues(list => {
                                            const current = list[index];
                                            current.name = tierValue.name ?? "";
                                            current.values = tierValue.values ?? [];
                                            current.description = tierValue.description ?? "";
                                            return Array.from(list);
                                        })}
                                        onDelete={() => {
                                            setSlaValues(list => {
                                                list.splice(index, 1);
                                                return Array.from(list);
                                            });
                                        }}
                                    />
                                ))}

                            </div>
                            <div className="d-flex justify-content-end align-items-center mt-3">

                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={createNewTier}
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Add SLA Policy
                                </button>
                            </div>

                        </>
                    )}

                </div>
            )}

            {dirtyTracker.saveComponent}

        </form>
    );
}
